/**
 * <lazy-app-blocks>
 *
 * Defers server-rendered app block markup until the visitor is about to
 * see it. The markup is kept inert inside a <template> so any inline or
 * external <script> tags (e.g. the Mimeeq configurator embed) do not
 * execute on page load.
 *
 * Activation triggers (whichever happens first):
 *   1. Click on any link pointing at the trigger anchor
 *      (e.g. the "build & price" button -> #builder).
 *   2. The section scrolling to within `data-root-margin` px of the
 *      viewport (IntersectionObserver).
 *   3. Idle fallback: `data-fallback-delay` seconds after page load.
 *      Mimeeq's global watchdog (embed.js, injected in <head> on any
 *      page with the app block) reports APP_NOT_LOADING and opens an
 *      error modal if the app hasn't loaded 20s after page load, so
 *      the block must not stay inert past that deadline. The fallback
 *      keeps initial page-load performance intact while guaranteeing
 *      the app is up well inside the 20s window.
 *
 * On activation the template content is inserted and its <script> tags
 * are re-created so they execute exactly as if rendered normally.
 * In the theme editor the section renders without this wrapper, so the
 * block stays editable/visible there.
 */
if (!customElements.get('lazy-app-blocks')) {
  customElements.define(
    'lazy-app-blocks',
    class LazyAppBlocks extends HTMLElement {
      connectedCallback() {
        if (this.dataset.loaded === 'true') return;

        if (window.Shopify && window.Shopify.designMode) {
          this.activate();
          return;
        }

        const margin = parseInt(this.dataset.rootMargin || '800', 10);
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.activate();
          },
          { rootMargin: `${margin}px 0px` }
        );
        this.observer.observe(this);

        const anchor = (this.dataset.triggerAnchor || '').trim();
        if (anchor) {
          this.clickHandler = (event) => {
            if (event.target.closest(`a[href$="#${anchor}"]`)) this.activate();
          };
          // Capture phase: insert content before the browser performs the
          // anchor jump so the scroll target has (reserved) height.
          document.addEventListener('click', this.clickHandler, true);
        }

        // Idle fallback — must stay comfortably under Mimeeq's 20s
        // page-load watchdog. 0 disables it (only safe if the Mimeeq
        // app block is not used in this section).
        const fallbackSeconds = parseFloat(this.dataset.fallbackDelay || '8');
        if (fallbackSeconds > 0) {
          const arm = () => {
            this.fallbackTimer = setTimeout(
              () => this.activate(),
              fallbackSeconds * 1000
            );
          };
          if (document.readyState === 'complete') {
            arm();
          } else {
            this.loadHandler = arm;
            window.addEventListener('load', this.loadHandler, { once: true });
          }
        }

        // Suppress Mimeeq's spurious APP_NOT_LOADING error modal while
        // the block is still intentionally inert. Registered at page
        // load, so this capture listener runs ahead of the modal
        // listener Mimeeq adds when its bundle loads.
        this.errorModalHandler = (event) => {
          const type = event.detail && event.detail.errorType;
          if (type === 'embed' && this.dataset.loaded !== 'true') {
            event.stopImmediatePropagation();
          }
        };
        document.addEventListener(
          'mimeeq-shopify-open-error-modal',
          this.errorModalHandler,
          true
        );
      }

      disconnectedCallback() {
        this.teardown();
      }

      teardown() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }
        if (this.clickHandler) {
          document.removeEventListener('click', this.clickHandler, true);
          this.clickHandler = null;
        }
        if (this.fallbackTimer) {
          clearTimeout(this.fallbackTimer);
          this.fallbackTimer = null;
        }
        if (this.loadHandler) {
          window.removeEventListener('load', this.loadHandler);
          this.loadHandler = null;
        }
        // Note: errorModalHandler is deliberately NOT removed here — it
        // guards until activation flips dataset.loaded, then no-ops.
      }

      activate() {
        if (this.dataset.loaded === 'true') return;
        this.dataset.loaded = 'true';
        this.teardown();

        const template = this.querySelector('template[data-lazy-app-blocks]');
        if (!template) return;

        const fragment = template.content.cloneNode(true);

        // Scripts cloned out of a <template> never execute; re-create them
        // so app embeds initialise.
        fragment.querySelectorAll('script').forEach((inert) => {
          const script = document.createElement('script');
          Array.from(inert.attributes).forEach((attr) =>
            script.setAttribute(attr.name, attr.value)
          );
          script.textContent = inert.textContent;
          inert.replaceWith(script);
        });

        template.replaceWith(fragment);
        this.dispatchEvent(
          new CustomEvent('lazy-app-blocks:loaded', { bubbles: true })
        );
      }
    }
  );
}
